/**
 * @readonly
 * @enum {string}
 */
export const MarkerTypes = {
    snapPointMarker: 'snap-point-marker',
    snapPointMarkerHover: 'snap-point-marker-hover',
    snapPointMarkerPerm: 'snap-point-marker-perm',
    snapPointMarkerPermHover: 'snap-point-marker-perm-hover',
    snapPointAll: 'snap-point-all',
    rectangleMarker: 'rectangle-marker'
};
Object.freeze(MarkerTypes);

/**
 * Helper class for VsCode intellisence, SVGElement(marker) used in atlas-selection contains some extra data
 */
export class SVGElementExt extends SVGElement {
    constructor() {
        super();
        /** @type {string?} */
        this.SPLM_PsId = undefined;
        /** @type {import('./atlasSelectionUtils').LocData?} */
        this.SPLM_Loc = undefined;
    }
}

/**
 * Area selection rectangle marker
 *
 * @type {SVGRectElement}
 */
let rectMarker = undefined;
/**
 * This function changes the marker style into the 'highlight' marker style
 *
 * @param {object} marker - a svg marker object
 */
export function highlightMarker(marker) {
    if (marker) {
        let cxStr = marker.getAttribute('cx').slice(0, -2);
        let cyStr = marker.getAttribute('cy').slice(0, -2);
        // let xFormStr = 'translate(' + cxStr + ' ' + cyStr + ') scale(1.3 1.3) translate(-' + cxStr + ' -' + cyStr + ')';
        let xFormStr = 'translate(' + cxStr + ' ' + cyStr + ') scale(1.0 1.0) translate(-' + cxStr + ' -' + cyStr + ')';
        marker.setAttribute('transform', xFormStr);
        if (marker.classList.contains(MarkerTypes.snapPointMarkerPerm)) {
            marker.classList.add(MarkerTypes.snapPointMarkerPermHover);
        } else {
            marker.classList.add(MarkerTypes.snapPointMarkerHover);
        }
    }
}

/**
 * getViewDiagonal gets the diagonal size of the svgOverlay object
 *
 * @param {HTMLElement} svgOverlay - a svgOverlay object
 * @returns {number} diagonal size or undefined if svgOverlay is invalid
 */
export function getViewDiagonal(svgOverlay) {
    if (svgOverlay) {
        let box = svgOverlay.getBoundingClientRect();
        var width = box.right - box.left;
        var height = box.bottom - box.top;
        return Math.sqrt(width * width + height * height);
    }
    return undefined;
}

/**
 * setLoc2D performs the transforation for loc.
 *
 * @param {object} plmVisWeb - PLMVisWeb module itself
 * @param {object} viewer - viewer object
 * @param {HTMLElement} svgOverlay - a svg overlay object
 * @param {string} psId - a PLMVisWeb object psId
 * @param {import('./atlasSelectionUtils').LocData} loc - a point object containing the following properties: location, marker, parentId, type, xPx, and yPx
 * @param {THREE.Matrix4} [worldMatrix] - a transformation matrix. If undefined, the transformation matrix from the parent object is used
 */
export function setLoc2D(plmVisWeb, viewer, svgOverlay, psId, loc, worldMatrix) {
    let worldPt = new plmVisWeb.THREE.Vector3();

    if (loc.isPrototypeSpace) {
        if (!worldMatrix) {
            worldMatrix = new plmVisWeb.THREE.Matrix4();
            const worldMatrixArray = viewer.getWorldMatrixByPsId(psId);
            worldMatrix.fromArray(worldMatrixArray);
        }
        worldPt.copy(loc.location).applyMatrix4(worldMatrix);
    } else {
        worldPt.copy(loc.location);
    }

    let screenCoords = viewer.projectModelPointToViewCoordinate(worldPt.x, worldPt.y, worldPt.z);

    loc.xPx = screenCoords[0];
    loc.yPx = screenCoords[1];
}

/**
 *
 * @param {object} PLMVisWeb
 * @param {object} viewer
 * @param {HTMLElement} svgOverlay
 * @param {any} markRadius
 * @param {string} psId
 * @param {import('./atlasSelectionUtils').LocData} loc
 * @param {THREE.Matrix4} [worldMatrix]
 * @returns
 */
export function displayMarker(PLMVisWeb, viewer, svgOverlay, markRadius, psId, loc, worldMatrix) {
    if (loc.marker) {
        //don't keep generating center markers
        return;
    }

    // Sometimes, when the geometry is modeled away such as the case in PR10815473, the client can still
    // select the geometry. The geometry is there at the time of selection, but when the modeling operation
    // finishes, the geometry is modeled away. In this case, when the command finishes, it needs to clean
    // up the selected and it fails when encountering the modeled away geometry.
    // We should filter out these kinds of geometry - don't do any thing
    if (viewer.getObjectTypeByPsId(psId) === '') {
        return;
    }

    setLoc2D(PLMVisWeb, viewer, svgOverlay, psId, loc, worldMatrix);

    /**@type {object}*/
    let newMarker = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
    newMarker.classList.add(MarkerTypes.snapPointMarker);
    newMarker.setAttribute('cx', loc.xPx + 'px');
    newMarker.setAttribute('cy', loc.yPx + 'px');
    newMarker.setAttribute('rx', markRadius);
    newMarker.setAttribute('ry', markRadius);

    svgOverlay.appendChild(newMarker);
    newMarker = svgOverlay.lastElementChild;
    loc.marker = newMarker;
    newMarker.SPLM_PsId = psId;
    newMarker.SPLM_Loc = loc;
}

/**
 * Performs marker 'highlight' and changes mouse icon to arrowIcon if the mouse event is available.
 * This function assumes the viewer is the grandparent of the mouse event target
 *
 * @param {SVGElement} marker - a marker object
 * @param {object} e - mouse event data
 */
export function onMouseOver(marker, e = null) {
    highlightMarker(marker);

    // show arrow cursor style. Currently, everything is manipulated on the 'galaxy-3dviewer' element
    if (e !== null) {
        let gViewer = e.target.parentElement.parentElement;
        if (gViewer.localName.includes('swf3d-3dviewer') || gViewer.className.includes('swf3d-3dviewer')) {
            gViewer.classList.add('snap-point-cursor-hover');
        }
    }
}

/**
 * 'De-highlights' the marker and resets the mouse icon to normal mouse icon if the mouse event is available.
 * This function assumes the viewer is the grandparent of the mouse event target
 *
 * @param {SVGElement} marker - a marker object
 * @param {object} e - mouse event data
 */
export function onMouseOut(marker, e = null) {
    if (marker) {
        marker.setAttribute('transform', 'scale(1 1)');
        if (marker.classList.contains(MarkerTypes.snapPointMarkerPerm)) {
            marker.classList.remove(MarkerTypes.snapPointMarkerPermHover);
        } else {
            marker.classList.remove(MarkerTypes.snapPointMarkerHover);
        }
    }

    if (e !== null) {
        let gViewer = e.target.parentElement.parentElement;
        gViewer.classList.remove('snap-point-cursor-hover');
    }
}

/**
 * Hides the marker
 *
 * @param {SVGElement} marker - a marker object
 */
export function hideMarker(marker) {
    if (marker) {
        marker.style.display = 'none';
    }
}

/**
 * Hides the marker of markerType
 *
 * @param {SVGElement} marker - a marker object
 * @param {MarkerTypes} markerType - marker type defined in MarkerTypes
 */
export function hideMarkerOfType(marker, markerType) {
    if (marker === null || marker === undefined) {
        return;
    }

    let hideIt = false;
    if (markerType === MarkerTypes.snapPointAll && marker.classList && marker.classList[0]) {
        hideIt = true;
    } else if (marker.classList && marker.classList[0] === markerType) {
        hideIt = true;
    }
    if (hideIt) {
        hideMarker(marker);
    }
}

/**
 * Hides all markers of markerType. If not supplied, it hides all the markers
 *
 * @param {HTMLElement} svgOverlay - a svg overlay object
 * @param {MarkerTypes} markerType - type of markers to hide. Defined in MarkerTypes
 */
export function hideMarkers(svgOverlay, markerType = MarkerTypes.snapPointAll) {
    let children = Array.from(svgOverlay.childNodes);
    for (let marker of children) {
        hideMarkerOfType(/**@type {SVGElement}*/ (marker), markerType);
    }
}

/**
 * Updates a marker
 *
 * @param {object} plmVisWeb - PLMVisWeb module itself
 * @param {object} viewer - viewer object
 * @param {HTMLElement} svgOverlay - a svg overlay object
 * @param {SVGElementExt} marker - a marker object
 * @param {number} markRadius - the radius of the marker
 */
export function updateMarker(plmVisWeb, viewer, svgOverlay, marker, markRadius) {
    if (!marker.SPLM_Loc) {
        // some markers may not have this property(e.g. rectangle marker for area select)
        return;
    }
    setLoc2D(plmVisWeb, viewer, svgOverlay, marker.SPLM_PsId, marker.SPLM_Loc);
    marker.setAttribute('cx', marker.SPLM_Loc.xPx + 'px');
    marker.setAttribute('cy', marker.SPLM_Loc.yPx + 'px');
    marker.setAttribute('rx', markRadius);
    marker.setAttribute('ry', markRadius);
    marker.style.display = 'inline';
}

/**
 * Updates a marker of makerType
 *
 * @param {object} plmVisWeb - PLMVisWeb module itself
 * @param {object} viewer - viewer object
 * @param {HTMLElement} svgOverlay - a svg overlay object
 * @param {SVGElementExt} marker - a marker object
 * @param {number} markRadius - radius of the marker
 * @param {MarkerTypes} markerType - type of marker to update
 */
export function updateMarkerOfType(plmVisWeb, viewer, svgOverlay, marker, markRadius, markerType) {
    let updateIt = false;
    let isHover = false;
    if (markerType === MarkerTypes.snapPointAll) {
        updateIt = true;
        if (
            marker.classList &&
            (marker.classList[0] === MarkerTypes.snapPointMarkerHover || marker.classList[0] === MarkerTypes.snapPointMarkerPermHover)
        ) {
            isHover = true;
        }
    } else if (marker.classList && marker.classList[0] === markerType) {
        updateIt = true;
        if (
            marker.classList &&
            (marker.classList[0] === MarkerTypes.snapPointMarkerHover || marker.classList[0] === MarkerTypes.snapPointMarkerPermHover)
        ) {
            isHover = true;
        }
    }
    if (isHover) {
        onMouseOut(marker);
    }
    if (updateIt) {
        updateMarker(plmVisWeb, viewer, svgOverlay, marker, markRadius);
    }
    if (isHover) {
        onMouseOver(marker);
    }
}

/**
 * Updates markers of markerType. If markerType is not supplied, all markers are updated
 *
 * @param {object} plmVisWeb - PLMVisWeb module itself
 * @param {object} viewer - viewer object
 * @param {HTMLElement} svgOverlay - a svg overlay object
 * @param {number} markRadius - radius of markers
 * @param {MarkerTypes} markerType - type of marker to update
 */
export function updateMarkers(plmVisWeb, viewer, svgOverlay, markRadius, markerType = MarkerTypes.snapPointAll) {
    if (plmVisWeb && viewer && svgOverlay) {
        let children = /** @type {SVGElementExt[]} */ (Array.from(svgOverlay.childNodes));
        for (let marker of children) {
            updateMarkerOfType(plmVisWeb, viewer, svgOverlay, marker, markRadius, markerType);
        }
    }
}

/**
 * Clears markers. However for center markers, hide them if keepCenter === true
 *
 * @param {HTMLElement} svgOverlay - a svgOverlay object
 * @param {boolean} [keep] - true to hide center markers and false to clear them
 */
export function clearMarkers(svgOverlay, keep) {
    if (!svgOverlay) {
        return;
    }
    let children = /** @type {SVGElementExt[]} */ (Array.from(svgOverlay.childNodes));
    for (let marker of children) {
        let remove = true;
        if (marker.classList && marker.classList[0] === MarkerTypes.snapPointMarker) {
            if (marker.SPLM_Loc) {
                if (keep && (marker.SPLM_Loc.type === 'Center' || marker.SPLM_Loc.type === 'Pole')) {
                    remove = false;
                    onMouseOut(marker);
                } else {
                    marker.SPLM_Loc.marker = null;
                }
            }
            if (remove) {
                svgOverlay.removeChild(marker);
            }
        }
    }
}

/**
 * Clears face markers.
 *
 * @param {HTMLElement} svgOverlay - a svgOverlay object
 * @param {string} locType - 'SnapPoint', 'SnapOnFace'
 */
export function clearMarkersOfLocType(svgOverlay, locType) {
    if (!svgOverlay) {
        return;
    }
    let children = /** @type {SVGElementExt[]} */ (Array.from(svgOverlay.childNodes));
    for (let marker of children) {
        let remove = true;
        if (marker.classList && marker.classList[0] === MarkerTypes.snapPointMarker) {
            if (marker.SPLM_Loc) {
                if (marker.SPLM_Loc.type === locType) {
                    marker.SPLM_Loc.marker = null;
                } else {
                    remove = false;
                    onMouseOut(marker);
                }
            }
            if (remove) {
                svgOverlay.removeChild(marker);
            }
        }
    }
}

/**
 *  Perform marker display change when selObject is selected/deselected
 *
 *  @param {HTMLElement} svgOverlay - a svgOverlay object
 *  @param {object} selObject - the point object to be selected/deselected
 *  @param {boolean} select - true to select and false to deselect the point object
 */
export function onSelect(svgOverlay, selObject, select) {
    let marker = selObject.marker;
    if (marker) {
        onMouseOut(marker);
        if (marker.SPLM_Loc) {
            marker.SPLM_Loc.marker = undefined;
        }
        selObject.marker = undefined;
        // there is a bug somewhere when changing the active
        // element list, the marker gets removed.  Need to hunt
        // it down.
        svgOverlay.removeChild(marker);
    }

    // test the point recipe by creating a block at the point using the point or its location.
    // Change testBlock = true to see the block created when selecting the point on edge or the center of arc edge
    //    let testBlock = false;
    //    if( testBlock && select ) {
    //    createBlock( selObject );
    //    }
}

/**
 *
 * @param {MouseEvent} e
 * @param {import('./AtlasSelection').MouseLoc} loc
 */
export function displayRectangle(e, loc) {
    let width = Math.abs(e.offsetX - loc.x);
    let height = Math.abs(e.offsetY - loc.y);
    let x = '';
    let y = '';
    let strokeDasharray = '';
    let fill = '';
    if (e.offsetX - loc.x >= 0) {
        x = loc.x + 'px';
        strokeDasharray = '';
        fill = 'rgba(50, 150, 185, 0.1)';
    } else {
        x = e.offsetX + 'px';
        strokeDasharray = '5,5';
        fill = 'rgba(50, 150, 185, 0.4)';
    }
    if (e.offsetY - loc.y >= 0) {
        y = loc.y + 'px';
    } else {
        y = e.offsetY + 'px';
    }
    if (rectMarker !== undefined) {
        updateRectMarker(x, y, width, height, fill, strokeDasharray);
    } else {
        rectMarker = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rectMarker.classList.add(MarkerTypes.rectangleMarker);

        rectMarker.setAttribute('x', x);
        rectMarker.setAttribute('y', y);
        rectMarker.setAttribute('width', width);
        rectMarker.setAttribute('height', height);
        rectMarker.style.fill = fill;
        rectMarker.style.strokeWidth = 1;
        rectMarker.style.stroke = '#3296B9';
        rectMarker.style.strokeWidth = 1;
        let svgOverlay = document.getElementById('svg-overlay');
        svgOverlay.appendChild(rectMarker);
    }
}

/**
 *
 * @param x
 * @param y
 * @param width
 * @param height
 * @param fill
 * @param strokeDasharray
 */
function updateRectMarker(x, y, width, height, fill, strokeDasharray) {
    rectMarker.setAttribute('x', x);
    rectMarker.setAttribute('y', y);
    rectMarker.setAttribute('width', width);
    rectMarker.setAttribute('height', height);
    rectMarker.style.fill = fill;
    rectMarker.style.stroke = '#3296B9';
    rectMarker.style.strokeWidth = 1;
    rectMarker.style.strokeDasharray = strokeDasharray;
}

/**
 *
 */
export function clearMarkersOfRecType() {
    if (rectMarker === undefined) {
        return;
    }
    let svgOverlay = rectMarker.parentNode;
    if (!svgOverlay) {
        return;
    }
    svgOverlay.removeChild(rectMarker);
    rectMarker = undefined;
}

export default {
    MarkerTypes,
    highlightMarker,
    getViewDiagonal,
    setLoc2D,
    displayMarker,
    onMouseOver,
    onMouseOut,
    hideMarker,
    hideMarkerOfType,
    hideMarkers,
    updateMarker,
    updateMarkerOfType,
    updateMarkers,
    clearMarkers,
    clearMarkersOfLocType,
    onSelect,
    displayRectangle,
    clearMarkersOfRecType
};
